import { Checkbox, Divider, IconButton, Radio, RadioGroup } from '@ads/components-react';
import { TrashIcon } from '@heroicons/react/24/outline';

import type { QuestionDetail } from '../../../../hooks/useQuestionEdit';

export interface AlternativesTableProps {
  readonly alternatives: QuestionDetail['alternatives'];
  readonly columnHeader: string;
  readonly deleteAriaLabel: string;
  readonly onDelete: (id: string) => void;
  readonly selectedCorrectIds: readonly string[];
  readonly onCorrectChange: (id: string) => void;
  /** Quando `true`, renderiza checkboxes (múltipla escolha); senão, radios. */
  readonly isMultiple?: boolean;
}

export function AlternativesTable({
  alternatives,
  columnHeader,
  deleteAriaLabel,
  onDelete,
  selectedCorrectIds,
  onCorrectChange,
  isMultiple = false,
}: AlternativesTableProps) {
  if (alternatives.length === 0) return null;

  const rows = alternatives.map((alt, idx) => (
    <div key={alt.id} className="flex w-full flex-col">
      {/* biome-ignore lint/a11y/noLabelWithoutControl: label wraps ADS Checkbox/Radio that render native inputs internally */}
      <label className="flex cursor-pointer items-center gap-micro rounded-sm px-xxs py-micro transition-all hover:bg-ctx-interactive-secondaryHover">
        {isMultiple ? (
          <Checkbox
            size="md"
            checked={selectedCorrectIds.includes(alt.id)}
            onCheckedChange={() => onCorrectChange(alt.id)}
          />
        ) : (
          <Radio size="md" value={alt.id} />
        )}

        <span
          className="[&_.code-display]:bg-gray-100 [&_.code-display]:px-2 [&_.code-display]:py-1 [&_.code-display]:font-mono [&_.code-display]:text-gray-800 text-ctx-content-base ts-paragraph-xs [&_*]:!font-base [&_*]:!text-xs [&_*]:!leading-lg [&_*]:!text-ctx-content-base [&_.code-display]:rounded-sm [&_.code-display]:text-sm" // Important is used to prevent the dangerouslySetInnerHTML from overriding some of the styles
          dangerouslySetInnerHTML={{
            __html: alt.value ?? '',
          }}
        />
        <IconButton
          icon={TrashIcon}
          ariaLabel={deleteAriaLabel}
          hierarchy="tertiary"
          size="sm"
          className="ml-auto"
          onClick={() => onDelete(alt.id)}
        />
      </label>
      {idx !== alternatives.length - 1 && <Divider />}
    </div>
  ));

  return (
    <div className="overflow-hidden rounded-sm border-hairline border-ctx-layout-border">
      <div className="flex items-center border-b-hairline border-ctx-layout-border bg-ctx-layout-body px-mini py-nano">
        <span className="text-ctx-content-title ts-subtitle-xxs">{columnHeader}</span>
      </div>

      {isMultiple ? (
        <div className="divide-y at-scrollbar max-h-[320px] divide-ctx-layout-border overflow-y-auto">
          {rows}
        </div>
      ) : (
        <RadioGroup
          value={selectedCorrectIds[0] ?? ''}
          onValueChange={onCorrectChange}
          className="divide-y at-scrollbar max-h-[320px] divide-ctx-layout-border overflow-y-auto"
        >
          {rows}
        </RadioGroup>
      )}
    </div>
  );
}
